import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useLocation, Outlet } from 'react-router-dom';
import { Provider } from 'react-redux';
import './i18n';
import './App.css';
import './theme/themes.css';
import { store } from './store';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { checkAuthStatus } from './store/slices/authSlice';
import Header from './components/Header';
import Footer from './components/Footer';
import LogoutSuccessBanner from './components/LogoutSuccessBanner';
import GlobalErrorBanner from './components/GlobalErrorBanner';
import Navigation from './components/portal/Navigation';
import NotFound from './pages/NotFound';
import HomeLandingPage from './pages/HomeLandingPage';
import StudentDashboard from './pages/portal/StudentDashboard';
import TeacherDashboard from './pages/portal/TeacherDashboard';
import TeacherStudents from './pages/portal/teacher/TeacherStudents';
import TeacherStudentDetail from './pages/portal/teacher/TeacherStudentDetail';
import TeacherPayments from './pages/portal/teacher/TeacherPayments';
import TeacherExams from './pages/portal/teacher/TeacherExams';
import TeacherExamDetail from './pages/portal/teacher/TeacherExamDetail';
import UnifiedLessonDetail from './pages/portal/common/UnifiedLessonDetail';
import UnifiedLessons from './pages/portal/common/UnifiedLessons';
import Profile from './pages/portal/Profile';
import StudentExams from './pages/portal/StudentExams';
import StudentExamDetail from './pages/portal/StudentExamDetail';
import StudentAvailableExams from './pages/portal/StudentAvailableExams';
import StudentPaymentDetailsPage from './pages/portal/StudentPaymentDetailsPage';
import StudentStatistics from './pages/portal/StudentStatistics';
import TeacherStatistics from './pages/portal/teacher/TeacherStatistics';
import TeacherPublicReviews from './pages/portal/TeacherPublicReviews';
import ProtectedRoute from './components/portal/ProtectedRoute';
import { UserRole } from './types/portal';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ParametersProvider } from './contexts/ParametersContext';
import SetPassword from './pages/SetPassword';
import VerifyEmail from './pages/VerifyEmail';
import SetPasswordRequired from './pages/portal/SetPasswordRequired';
import ForgotPassword from './pages/portal/ForgotPassword';

function AppShell() {
    const dispatch = useAppDispatch();
    const { isLoading } = useAppSelector((state) => state.auth);
    const location = useLocation();

    useEffect(() => {
        // Only check auth if we previously had an authenticated session to avoid flashing loader on public pages
        let shouldCheck = false;
        try {
            if (typeof window !== 'undefined') {
                shouldCheck = localStorage.getItem('ud_isAuthenticated') === 'true';
            }
        } catch (_) { /* ignore storage errors */ }
        if (!shouldCheck) return;

        const id = setTimeout(() => {
            dispatch(checkAuthStatus());
        }, 0);
        return () => clearTimeout(id);
    }, [dispatch]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg">Loading...</div>
            </div>
        );
    }

    const isLoginPage = location.pathname === '/portal/login';
    const isRegisterPage = location.pathname === '/portal/register';
    const isForgotPasswordPage = location.pathname === '/portal/forgot-password';
    const isPortalRoute = location.pathname.startsWith('/portal') && !isLoginPage && !isRegisterPage && !isForgotPasswordPage;
    const hideFooter = isLoginPage || isRegisterPage || isForgotPasswordPage;

    return (
        <div className="App">
            <GlobalErrorBanner />
            <LogoutSuccessBanner />
            {!isPortalRoute && <Header />}
            {isPortalRoute && <Navigation />}
            {isPortalRoute ? (
                <div className="portal-top-pad">
                    <Outlet />
                </div>
            ) : (
                <Outlet />
            )}
            {/* Footer/WhatsApp shown only on public pages (not on auth pages) */}
            {!isPortalRoute && !hideFooter && <Footer />}
        </div>
    );
}

const router = createBrowserRouter([
    {
        path: '/',
        element: <AppShell />,
        children: [
            // Public pages removed; redirect index to portal login
            // { index: true, element: <Navigate to="/portal/login" replace /> },
            { path: '/', element: <HomeLandingPage /> },
            { path: 'portal/login', element: <Navigate to="/?auth=login" replace /> },
            { path: 'portal/register', element: <Navigate to="/?auth=register" replace /> },
            { path: 'portal/forgot-password', element: <ForgotPassword /> },
            { path: 'set-password', element: <SetPassword /> },
            { path: 'verify-email', element: <VerifyEmail /> },
                        // Unified lessons routes for all roles
                        { path: 'portal/lesson', element: (
                            <ProtectedRoute allowedRoles={[UserRole.STUDENT, UserRole.TEACHER, UserRole.ADMIN]}>
                                <UnifiedLessons />
                            </ProtectedRoute>
                        ) },
                        { path: 'portal/lesson/:lessonId', element: (
                            <ProtectedRoute allowedRoles={[UserRole.STUDENT, UserRole.TEACHER, UserRole.ADMIN]}>
                                <UnifiedLessonDetail />
                            </ProtectedRoute>
                        ) },
                        { path: 'portal/teacher/:teacherId/reviews', element: (
                            <ProtectedRoute allowedRoles={[UserRole.STUDENT, UserRole.TEACHER, UserRole.ADMIN]}>
                                <TeacherPublicReviews />
                            </ProtectedRoute>
                        ) },
            // Student
            { path: 'portal/student', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT]}>
                    <StudentDashboard />
                </ProtectedRoute>
            ) },
            { path: 'portal/student/lesson', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT, UserRole.TEACHER, UserRole.ADMIN]}>
                    <Navigate to="/portal/lesson" replace />
                </ProtectedRoute>
            ) },
            { path: 'portal/student/lesson/:lessonId', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT, UserRole.TEACHER, UserRole.ADMIN]}>
                    <Navigate to="/portal/lesson" replace />
                </ProtectedRoute>
            ) },
            { path: 'portal/student/exams', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT]}>
                    <StudentExams />
                </ProtectedRoute>
            ) },
            { path: 'portal/student/statistics', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT]}>
                    <React.Suspense fallback={null}>
                        <StudentStatistics />
                    </React.Suspense>
                </ProtectedRoute>
            ) },
            { path: 'portal/student/exams/available', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT]}>
                    <StudentAvailableExams />
                </ProtectedRoute>
            ) },
            { path: 'portal/student/exams/:examId', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT]}>
                    <StudentExamDetail />
                </ProtectedRoute>
            ) },
            { path: 'portal/student/payments', element: <Navigate to="/portal/student/agenda" replace /> },
            { path: 'portal/student/agenda', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT]}>
                    <StudentPaymentDetailsPage />
                </ProtectedRoute>
            ) },
            { path: 'student/lesson/:lessonId', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT, UserRole.TEACHER, UserRole.ADMIN]}>
                    <Navigate to="/portal/lesson" replace />
                </ProtectedRoute>
            ) },
            { path: 'portal/profile', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT, UserRole.TEACHER, UserRole.ADMIN]}>
                    <Profile />
                </ProtectedRoute>
            ) },
            { path: 'portal/set-password', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT, UserRole.TEACHER, UserRole.ADMIN]}>
                    <SetPasswordRequired />
                </ProtectedRoute>
            ) },
            { path: 'portal/student/profile', element: <Navigate to="/portal/profile" replace /> },
            // Teacher
            { path: 'portal/teacher', element: (
                <ProtectedRoute allowedRoles={[UserRole.TEACHER]}>
                    <TeacherDashboard />
                </ProtectedRoute>
            ) },
            { path: 'portal/teacher/profile', element: <Navigate to="/portal/profile" replace /> },
            { path: 'portal/teacher/lessons', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT, UserRole.TEACHER, UserRole.ADMIN]}>
                    <Navigate to="/portal/lesson" replace />
                </ProtectedRoute>
            ) },
            { path: 'portal/teacher/students', element: (
                <ProtectedRoute allowedRoles={[UserRole.TEACHER]}>
                    <TeacherStudents />
                </ProtectedRoute>
            ) },
            { path: 'portal/teacher/student/:studentId', element: (
                <ProtectedRoute allowedRoles={[UserRole.TEACHER]}>
                    <TeacherStudentDetail />
                </ProtectedRoute>
            ) },
            { path: 'portal/teacher/payments', element: (
                <ProtectedRoute allowedRoles={[UserRole.TEACHER]}>
                    <TeacherPayments />
                </ProtectedRoute>
            ) },
            { path: 'portal/teacher/exams', element: (
                <ProtectedRoute allowedRoles={[UserRole.TEACHER]}>
                    <TeacherExams />
                </ProtectedRoute>
            ) },
            { path: 'portal/teacher/statistics', element: (
                <ProtectedRoute allowedRoles={[UserRole.TEACHER]}>
                    <React.Suspense fallback={null}>
                        <TeacherStatistics />
                    </React.Suspense>
                </ProtectedRoute>
            ) },
            { path: 'portal/teacher/exams/:examId', element: (
                <ProtectedRoute allowedRoles={[UserRole.TEACHER]}>
                    <TeacherExamDetail />
                </ProtectedRoute>
            ) },
            { path: 'portal/teacher/lesson/:lessonId', element: (
                <ProtectedRoute allowedRoles={[UserRole.STUDENT, UserRole.TEACHER, UserRole.ADMIN]}>
                    <Navigate to="/portal/lesson/:lessonId" replace />
                </ProtectedRoute>
            ) },
            { path: '*', element: <NotFound /> },
        ],
    },
]);

function App() {
    return (
        <Provider store={store}>
            <ThemeProvider>
                <LanguageProvider>
                    <ParametersProvider>
                        <RouterProvider router={router} future={{ v7_startTransition: true }} />
                    </ParametersProvider>
                </LanguageProvider>
            </ThemeProvider>
        </Provider>
    );
}

export { AppShell };
export default App;